import React, { useContext, useEffect } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@material-ui/core';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { MAIN_CONTAINER_ID } from '../../../../constants';
import { DeviceContext } from '../../../../contexts';
import { AgreementBusinessType } from '../../../../types';
import { AppTextField } from '../../../../components/inputs';
import { ControlledDateField, ControlledSelect } from '../../../../components/controlled-inputs';
import { ButtonTitleWithLoading } from '../../../../components/ButtonTitleWithLoading';

interface BusinessTypeOption {
  name: string;
  value: AgreementBusinessType;
}

const BUSINESS_TYPE_OPTIONS: BusinessTypeOption[] = [
  { name: 'Sole trader', value: 'sole-trader' },
  { name: 'Company', value: 'company' },
  { name: 'Partnership', value: 'partnership' },
  { name: 'Trust', value: 'trust' },
];

export interface ReissueFormValues {
  businessType: AgreementBusinessType;
  // YYYY-MM-DD, ready for the API.
  agreementDate: string;
  note?: string;
}

interface Form {
  business_type: BusinessTypeOption | '';
  agreement_date: string;
  note: string;
}

interface Props {
  open: boolean;
  loading: boolean;
  handleClose: () => void;
  handleReissue: (values: ReissueFormValues) => void;
}

export const ReissueAgreementDialog: React.FC<Props> = ({ open, loading, handleClose, handleReissue }) => {
  const { isMobile } = useContext(DeviceContext);
  const theme = useTheme();

  const methods = useForm<Form>({
    defaultValues: { business_type: '', agreement_date: '', note: '' },
  });
  const { handleSubmit, control, reset } = methods;

  useEffect(() => {
    if (open) {
      reset({ business_type: '', agreement_date: '', note: '' });
    }
  }, [open, reset]);

  const onSubmit = (data: Form) => {
    if (!data.business_type) {
      return;
    }
    handleReissue({
      businessType: data.business_type.value,
      agreementDate: data.agreement_date,
      note: data.note?.trim() || undefined,
    });
  };

  return (
    <Dialog
      fullScreen={isMobile}
      open={open}
      maxWidth="sm"
      fullWidth
      container={document.getElementById(MAIN_CONTAINER_ID)}
      onClose={handleClose}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <DialogTitle>Reissue agreement</DialogTitle>

          <DialogContent>
            <Box
              padding={2}
              marginBottom={2}
              borderRadius={8}
              border={`1px solid ${theme.palette.warning.main}`}
            >
              <Typography variant="body2">
                This reissues the agreement document only. It does not change the worker's business type on
                their profile. If the worker's business has actually changed, edit their profile instead — a
                new version is created automatically.
              </Typography>
            </Box>

            <ControlledSelect
              name="business_type"
              label="Business type"
              items={BUSINESS_TYPE_OPTIONS}
              margin="normal"
              rules={{ required: 'Business type is required' }}
            />

            <ControlledDateField name="agreement_date" label="Agreement date" margin="normal" />

            <Controller
              control={control}
              name="note"
              render={({ field }) => (
                <AppTextField
                  {...field}
                  multiline
                  fullWidth
                  margin="normal"
                  label="Note"
                  placeholder="Why are you reissuing this agreement?"
                />
              )}
            />
          </DialogContent>

          <DialogActions>
            <Button size="large" style={{ minWidth: 115 }} variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              size="large"
              style={{ minWidth: 115 }}
              color="primary"
              variant="contained"
              disabled={loading}
              type="submit"
            >
              <ButtonTitleWithLoading title="Reissue" loaderVariant="paper" loading={loading} />
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
